import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getEmailLogs, getEmailLogStats, retryFailedEmail } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, CheckCircle2, XCircle, Search, ChevronLeft, ChevronRight, BarChart3, Clock, AlertTriangle, Filter, RotateCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  offer_letter: 'Offer Letter',
  offer_letter_bulk: 'Offer Letter (Bulk)',
  onboarding: 'Onboarding',
  notification: 'Notification',
  exit_notification: 'Exit Notification',
  leave_reminder: 'Leave Reminder',
  probation: 'Probation',
};

const CATEGORY_COLORS = {
  offer_letter: 'bg-blue-100 text-blue-700',
  offer_letter_bulk: 'bg-indigo-100 text-indigo-700',
  onboarding: 'bg-amber-100 text-amber-700',
  notification: 'bg-slate-100 text-slate-700',
  exit_notification: 'bg-red-100 text-red-700',
  leave_reminder: 'bg-green-100 text-green-700',
  probation: 'bg-purple-100 text-purple-700',
};

export default function EmailLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categories, setCategories] = useState({});
  const [expandedLog, setExpandedLog] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await getEmailLogs(params);
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
      setCategories(res.data.categories || {});
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, categoryFilter, statusFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getEmailLogStats();
      setStats(res.data);
    } catch {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleRetry = async (logId, e) => {
    e.stopPropagation();
    setRetryingId(logId);
    try {
      const res = await retryFailedEmail(logId);
      if (res.data.success) {
        toast.success(res.data.message || 'Email re-sent successfully!');
        fetchLogs();
        fetchStats();
      } else {
        toast.error(res.data.error || 'Retry failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry email');
    } finally {
      setRetryingId(null);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <Layout user={user}>
      <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="email-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Delivery Log</h1>
            <p className="text-sm text-slate-500 mt-1">Track every email sent from the system</p>
          </div>
          <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }} data-testid="refresh-logs-btn">
            <Mail className="mr-2" size={16} /> Refresh
          </Button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50"><Mail className="text-blue-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total Emails</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50"><CheckCircle2 className="text-green-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold text-green-700" data-testid="stat-sent">{stats.sent}</p>
                  <p className="text-xs text-slate-500">Delivered</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-50"><XCircle className="text-red-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold text-red-700" data-testid="stat-failed">{stats.failed}</p>
                  <p className="text-xs text-slate-500">Failed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-50"><BarChart3 className="text-amber-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-rate">{stats.success_rate}%</p>
                  <p className="text-xs text-slate-500">Success Rate</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search by recipient, subject, or triggered by..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
                <Button type="submit" variant="outline" data-testid="search-btn">Search</Button>
              </form>
              <div className="flex gap-2">
                <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]" data-testid="category-filter">
                    <Filter size={14} className="mr-1" />
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(categories).map(([cat, count]) => (
                      <SelectItem key={cat} value={cat}>
                        {CATEGORY_LABELS[cat] || cat} ({count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[130px]" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs table */}
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-600">{total} emails found</CardTitle>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>Page {page} of {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronLeft size={16} />
                </Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading...</div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="mx-auto text-slate-300 mb-3" size={40} />
                <p className="text-slate-500">No email logs found</p>
                <p className="text-sm text-slate-400 mt-1">Emails will be logged here when sent from the system</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Recipient</th>
                      <th className="px-4 py-3">Subject</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Triggered By</th>
                      <th className="px-4 py-3">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logs.map((log, idx) => (
                      <React.Fragment key={log.id || idx}>
                        <tr
                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${log.status === 'failed' ? 'bg-red-50/40' : ''}`}
                          onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                          data-testid={`email-log-row-${idx}`}
                        >
                          <td className="px-4 py-3">
                            {log.status === 'sent' ? (
                              <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                                <CheckCircle2 size={15} /> Sent
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                <XCircle size={15} /> Failed
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-800 font-medium max-w-[200px] truncate" title={log.to_email}>
                            {log.to_email}
                          </td>
                          <td className="px-4 py-3 text-slate-600 max-w-[280px] truncate" title={log.subject}>
                            {log.subject}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[log.category] || 'bg-slate-100 text-slate-600'}`}>
                              {CATEGORY_LABELS[log.category] || log.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">{log.triggered_by || 'system'}</td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <Clock size={13} /> {formatTimestamp(log.timestamp)}
                            </span>
                          </td>
                        </tr>
                        {expandedLog === log.id && (
                          <tr>
                            <td colSpan={6} className="px-6 py-3 bg-slate-50 border-l-4 border-l-slate-300">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                <div>
                                  <span className="text-slate-400 block mb-0.5">Sent Via</span>
                                  <span className="text-slate-700 font-medium">{log.sent_via || 'smtp'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block mb-0.5">Source</span>
                                  <span className="text-slate-700 font-medium">{log.source || 'env'}</span>
                                </div>
                                {log.cc && log.cc.length > 0 && (
                                  <div>
                                    <span className="text-slate-400 block mb-0.5">CC</span>
                                    <span className="text-slate-700">{log.cc.join(', ')}</span>
                                  </div>
                                )}
                                {log.related_id && (
                                  <div>
                                    <span className="text-slate-400 block mb-0.5">Related ID</span>
                                    <span className="text-slate-700 font-mono text-[11px]">{log.related_id}</span>
                                  </div>
                                )}
                                {log.metadata && log.metadata.attachment && (
                                  <div>
                                    <span className="text-slate-400 block mb-0.5">Attachment</span>
                                    <span className="text-slate-700">{log.metadata.attachment}</span>
                                  </div>
                                )}
                              </div>
                              {log.error && (
                                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start gap-2">
                                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                  <span>{log.error}</span>
                                </div>
                              )}
                              {log.status === 'failed' && !log.retry_status && (
                                <div className="mt-3 flex items-center gap-3">
                                  <Button
                                    size="sm"
                                    onClick={(e) => handleRetry(log.id, e)}
                                    disabled={retryingId === log.id}
                                    className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8"
                                    data-testid={`retry-btn-${log.id}`}
                                  >
                                    {retryingId === log.id ? (
                                      <><Loader2 className="mr-1.5 animate-spin" size={13} /> Retrying...</>
                                    ) : (
                                      <><RotateCw className="mr-1.5" size={13} /> Retry Send</>
                                    )}
                                  </Button>
                                  <span className="text-xs text-slate-400">Re-sends to {log.to_email} using current SMTP settings</span>
                                </div>
                              )}
                              {log.retry_status === 'retried' && (
                                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 flex items-center gap-2">
                                  <CheckCircle2 size={14} />
                                  <span>Retried successfully{log.retried_by ? ` by ${log.retried_by}` : ''}{log.retried_at ? ` at ${formatTimestamp(log.retried_at)}` : ''}</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
